import { useAuth } from "@via/hooks/useAuth.tsx";
import { useGlobal } from "@via/hooks/useGlobal.tsx";
import { defaultOrganizationFor } from "@via/hooks/organization-access.ts";
import AdminPanel from "@via/pages/account/admin/admin.tsx";
import Credentials from "@via/pages/account/profile/credentials/credentials.tsx";
import Profile from "@via/pages/account/profile/profile.tsx";
import RunEnvironments from "@via/pages/account/profile/run-environments/run-environments.tsx";
import Preferences from "@via/pages/account/profile/user-profile/preferences/preferences.tsx";
import ChangePassword from "@via/pages/account/profile/user-profile/change-password/change-password.tsx";
import Security from "@via/pages/account/profile/security/security.tsx";
import UserGroups from "@via/pages/account/profile/user-groups/user-groups.tsx";
import Activity from "@via/pages/activity/activity.tsx";
import Apps from "@via/pages/apps/apps.tsx";
import ManageAppStatus from "@via/pages/apps/manage-status.tsx";
import MyApps from "@via/pages/apps/my-apps.tsx";
import ClusterLaunches from "@via/pages/apps/hpc/cluster-launches.tsx";
import AgentWorkspaces from "@via/pages/agent-workspaces/workspaces.tsx";
import AgentWorkspaceDetail from "@via/pages/agent-workspaces/workspace-detail.tsx";
import JobRequestReview from "@via/pages/agent-workspaces/job-request-review.tsx";
import NotFound from "@via/pages/authentication/404-not-found.tsx";
import InactiveOrganization from "@via/pages/authentication/inactive-organization.tsx";
import LogIn from "@via/pages/authentication/login.tsx";
import HomeRedirect from "@via/pages/authentication/home-redirect.tsx";
import MfaPage from "@via/pages/authentication/mfa/mfa-page.tsx";
import ResetPassword from "@via/pages/authentication/reset-password.tsx";
import Status from "@via/pages/status/status.tsx";
import SetNewPassword from "@via/pages/authentication/set-new-password.tsx";
// import SignUp from "@via/pages/authentication/signup.tsx";
import CreateDataSource from "@via/pages/data/data-connections/create-data-source.tsx";
import GeoSource from "@via/pages/data/data-sources/api-data-sources/geo/geo-source.tsx";
import UrlData from "@via/pages/data/data-sources/api-data-sources/url/url-data.tsx";
import { GoogleCloudStorageWrapper } from "@via/pages/data/data-sources/cloud-data-sources/google-cloud-storage/google-cloud-storage.tsx";
import RemoteStorageData from "@via/pages/data/data-sources/remote-storage/remote-storage-data.tsx";
import OAuthCallback from "@via/pages/data/data-sources/remote-storage/oauth-callback.tsx";
import { SshDataWrapper } from "@via/pages/data/data-sources/ssh-data-source/ssh-data.tsx";
import Data from "@via/pages/data/data.tsx";
import { pipelinesRoutes } from "@via/pages/pipelines/pipelines-routes.tsx";
import Dashboard from "@via/pages/projects/dashboard/dashboard.tsx";
import Metadata from "@via/pages/projects/metadata/metadata.tsx";
import PipelineAdvanced from "@via/pages/projects/pipelines/advanced/advanced.tsx";
import PipelineDescription from "@via/pages/projects/pipelines/description/description.tsx";
import Pipeline from "@via/pages/projects/pipelines/pipeline.tsx";
import Pipelines from "@via/pages/projects/pipelines/pipelines.tsx";
import PipelineSettings from "@via/pages/projects/pipelines/settings/settings.tsx";
import PipelineGit from "@via/pages/projects/pipelines/git/pipeline-git.tsx";
import PipelineProvenance from "@via/pages/projects/pipelines/provenance/pipeline-provenance.tsx";
import Workflow from "@via/pages/projects/pipelines/workflow/workflow.tsx";
import Portal from "@via/pages/projects/portal/portal.tsx";
import Project from "@via/pages/projects/project.tsx";
import Projects from "@via/pages/projects/projects.tsx";
import Run from "@via/pages/projects/runs/run.tsx";
import Runs from "@via/pages/projects/runs/runs.tsx";
import ProjectSettings from "@via/pages/projects/settings/settings.tsx";
import RunsHome from "@via/pages/runs/runs.tsx";
import SampleTrackers from "@via/pages/sample-trackers/sample-trackers.tsx";
import ScanPage from "@via/pages/scan/scan.tsx";
import AliasRedirect from "@via/pages/scan/alias-redirect.tsx";
import { ROUTES, USER_ROLES } from "@via/utils/constants.tsx";
import {
  shouldBlockAccess,
  shouldBlockOrgMemberAccess,
} from "@via/utils/helpers.tsx";
import {
  buildLoginUrl,
  isSafeRedirectPath,
  setPostLoginRedirect,
} from "@via/utils/post-login-redirect";
import { Fragment, type JSX, useEffect } from "react";
import {
  createBrowserRouter,
  Navigate,
  Outlet,
  RouterProvider,
  useLocation,
  useNavigate,
  useParams,
} from "react-router-dom";
import MainLayout from "./layout.tsx";
import IndividualFeatureFlags from "@via/pages/account/admin/feature-flags/individual-feature-flags.tsx";
import IntegrationTokens from "@via/pages/account/profile/integration-tokens/integration-tokens.tsx";
import DeactivateAccount from "@via/pages/account/profile/deactivate-account/deactivate-account.tsx";
import PersonalAccessTokens from "@via/pages/account/profile/personal-access-tokens/personal-access-tokens.tsx";
import ConnectAssistants from "@via/pages/account/profile/connect-ai/connect-ai.tsx";
import Config from "@via/utils/config.tsx";
import { useConfigurations } from "@via/api/configuration";
import GlobalErrorBoundaryWrapper from "@via/components/error-boundary/error-boundary.tsx";
import { ChangesDetectProvider } from "@via/context/changes-detect-context.tsx";
import { BaseSpace } from "@via/pages/data/data-sources/token-based-data-sources/basespace/basespace.tsx";
import PremiumDatasetList from "@via/pages/data/premium-dataset/premium-dataset-list.tsx";
import PremiumDatasetFile from "@via/pages/data/premium-dataset/premium-dataset-file.tsx";
import Workbenchs from "@via/pages/workbenchs/workbenchs.tsx";
import MyWorkbenchs from "@via/pages/workbenchs/my-workbenchs.tsx";
import ManageWorkbenchStatus from "@via/pages/workbenchs/manage-status.tsx";
import Cortex from "@via/pages/cortex/cortex.tsx";
import ModelGarden from "@via/pages/cortex/model-garden/model-garden.tsx";
import ModelSkill from "@via/pages/cortex/model-skill/model-skill.tsx";
import FoundryModels from "@via/pages/cortex/foundry-models/foundry-models.tsx";
import McpServer from "@via/pages/cortex/mcp-server/mcp-server.tsx";
import KnowledgeBuilder from "@via/pages/cortex/knowledge-builder/knowledge-builder.tsx";
import Users from "@via/pages/account/admin/users/users.tsx";
import SelfSignUp from "@via/pages/authentication/self-service-signup/signup.tsx";
import UrlInviteSignup from "@via/pages/authentication/url-invite-signup/url-invite-signup.tsx";
import GuestSignUp from "@via/pages/authentication/guest-signup/guest-signup.tsx";
import ContactSalesRequest from "@via/pages/authentication/self-service-signup/contact-sales-request.tsx";
import Organization from "@via/pages/organization/organization.tsx";
import OrganizationActivity from "@via/pages/organization/activity/activity.tsx";
import OrganizationUsage from "@via/pages/organization/usage/usage.tsx";
import OrganizationUsers from "@via/pages/organization/users/users.tsx";
import OrganizationSettings from "@via/pages/organization/settings/settings.tsx";
import SelfSignIn from "@via/pages/authentication/self-service-signin/self-signin.tsx";
import GuestSignIn from "@via/pages/authentication/guest-signin/guest-signin.tsx";
import CustomerBoundary from "@via/pages/account/admin/customer-boundary/customer-boundary.tsx";
import OrganizationRunEnvironments from "@via/pages/organization/run-environments/run-environments.tsx";
import OrganizationInvoices from "@via/pages/organization/invoices/invoices.tsx";
import Organizations from "@via/pages/account/admin/organizations/organizations.tsx";
import OrganizationChargeHistory from "@via/pages/account/admin/organization-charge-history/organization-charge-history.tsx";
import HealthCheck from "@via/pages/account/admin/health-check/health-check.tsx";
import NotificationSettings from "@via/pages/account/admin/notification-settings/notification-settings.tsx";
import Tags from "@via/pages/account/admin/tags/tags.tsx";
import ImportLogins from "@via/pages/account/admin/import-logins/import-logins.tsx";
import TrustedRepositories from "@via/pages/account/admin/trusted-repositories/trusted-repositories.tsx";
import VoucherCodes from "@via/pages/account/admin/voucher-codes/voucher-codes.tsx";
import { AmazonS3Wrapper } from "@via/pages/data/data-sources/cloud-data-sources/amazon-s3/amazon-s3.tsx";

const AuthProtectedRoutes = (): JSX.Element => {
  const { user } = useAuth();
  const { pathname, search } = useLocation();

  if (user === null) {
    return <Fragment />; // loading or unknown
  }

  if (user === false) {
    const target = pathname + search;
    const canRedirect = isSafeRedirectPath(target);
    setPostLoginRedirect(target);
    const loginTo = canRedirect ? buildLoginUrl(target) : ROUTES.LOGIN;
    return <Navigate to={loginTo} replace />;
  }

  // Additional logic for self-registered or invited users not fully onboarded
  // or
  // Inactive organizations for organization admins
  const blockAccess = shouldBlockAccess(user);

  if (blockAccess) {
    return <Navigate to={ROUTES.SELF_SIGNUP} replace />;
  }

  // Inactive organizations for organization members
  const blockOrgMemberAccess = shouldBlockOrgMemberAccess(user);
  if (blockOrgMemberAccess) {
    return <Navigate to={ROUTES.INACTIVE_ORGANIZATION} replace />;
  }

  return <Outlet />;
};

const AdminProtectedRoutes = (): JSX.Element => {
  const { user } = useAuth();
  switch (user.userRole) {
    case USER_ROLES.ADMIN:
      return <Outlet />;
    default:
      return <Navigate to={ROUTES.NOT_FOUND} replace />;
  }
};

const FeatureFlagProtectedRoutes = ({
  featureFlag,
}: {
  featureFlag: string;
}): JSX.Element => {
  const { user } = useAuth();

  if (user === null || user === false) {
    return <Fragment />; // loading or not authenticated
  }

  if (!user.featureFlags) {
    return <Fragment />; // full user data not yet loaded (e.g. right after login)
  }

  const isEnabled = user.featureFlags[featureFlag] === true;

  if (!isEnabled) {
    return <Navigate to={ROUTES.NOT_FOUND} replace />;
  }

  return <Outlet />;
};

const RunWrapper = () => {
  const { runId = "" } = useParams();
  // This will re-render the Run component completely whenever runId changes.
  return (
    <ChangesDetectProvider>
      <Run key={runId} />
    </ChangesDetectProvider>
  );
};

// The run page used to live at the singular `/run/:runId`. It now lives at
// `/runs/:runId`, so redirect the old path to keep existing bookmarks and
// shared links working. Query string and hash are preserved.
const RunRedirect = () => {
  const { runId = "" } = useParams();
  const { search, hash } = useLocation();
  return <Navigate replace to={`/runs/${runId}${search}${hash}`} />;
};

// so many place we redirect to /activity
// so we wrap it in a component to decide
// if we need to redirect to organization activity or not
const ActivityWrapper = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { selectedOrganization } = useGlobal();
  // The organization last worked in, not whichever membership sorts first (#1069).
  const organizationId =
    defaultOrganizationFor(user, selectedOrganization?.id)?.id || "";
  const isOrganizationActive = organizationId !== "";

  useEffect(() => {
    if (
      user &&
      "registrationType" in user &&
      user.registrationType !== null &&
      organizationId &&
      isOrganizationActive
    ) {
      navigate(`/organization/${organizationId}/activity`, { replace: true });
    }
  }, [user, organizationId, navigate]);

  if (
    user &&
    user.registrationType !== null &&
    user.registrationType !== "guest"
  ) {
    return null; // or a loading spinner
  }

  return <Activity />;
};

const OrganizationWrapper = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { selectedOrganization } = useGlobal();
  const organizationId =
    defaultOrganizationFor(user, selectedOrganization?.id)?.id || "";

  useEffect(() => {
    if (
      user &&
      "registrationType" in user &&
      user.registrationType !== null &&
      organizationId
    ) {
      navigate(`/organization/${organizationId}/activity`, { replace: true });
    }
  }, [user, organizationId, navigate]);

  if (user && user.registrationType !== null) {
    return null; // or a loading spinner
  }

  return <OrganizationWrapper />;
};

const InvoicesRoute = () => {
  const { data: configurations, isLoading } = useConfigurations();
  if (isLoading) return null;
  return configurations?.ENABLE_STRIPE ? (
    <OrganizationInvoices />
  ) : (
    <Navigate replace to="activity" />
  );
};

const router = createBrowserRouter(
  [
    {
      element: (
        <GlobalErrorBoundaryWrapper>
          <MainLayout />
        </GlobalErrorBoundaryWrapper>
      ),
      children: [
        {
          path: "/login",
          element: <LogIn />,
        },
        {
          path: "/login/mfa",
          element: <MfaPage />,
        },
        // {
        //   path: "/signup",
        //   element: <SignUp />,
        // },
        /**
         * TODO: Structure a better route logic
         */
        {
          path: "/self-signin",
          element: <SelfSignIn />,
        },
        {
          path: "/guest-signin",
          element: <GuestSignIn />,
        },
        {
          path: "/self-signup",
          element: <SelfSignUp />,
        },
        {
          path: "/guest-signup",
          element: <GuestSignUp />,
        },
        {
          path: "/join/:slug",
          element: <UrlInviteSignup />,
        },
        {
          path: "/contact-sales",
          element: <ContactSalesRequest />,
        },
        {
          path: "/reset-password",
          element: <ResetPassword />,
        },
        {
          path: "/auth/reset-password",
          element: <SetNewPassword />,
        },
        {
          // Public on purpose: a status page that needs a login cannot report the outage that
          // stopped you logging in. It exposes only up/down per component, never versions,
          // hostnames or counts, and it sits outside AuthProtectedRoutes to stay reachable.
          path: "/status",
          element: <Status />,
        },
        {
          path: "*",
          element: <NotFound />,
        },
        {
          path: "/inactive-organization",
          element: <InactiveOrganization />,
        },
        {
          element: <AuthProtectedRoutes />,
          children: [
            {
              path: "/404",
              element: <NotFound />,
            },
            {
              path: "/",
              element: <HomeRedirect />,
            },
            {
              element: <AdminProtectedRoutes />,
              children: [
                {
                  path: "admin",
                  element: <AdminPanel />,
                  children: [
                    {
                      index: true,
                      element: <Navigate replace to="users" />,
                    },
                    {
                      path: "notification-settings",
                      element: <NotificationSettings />,
                    },
                    {
                      path: "users",
                      element: <Users />,
                    },
                    {
                      path: "feature-flags",
                      element: <IndividualFeatureFlags />,
                    },
                    {
                      path: "customer-boundary",
                      element: <CustomerBoundary />,
                    },
                    {
                      path: "organizations",
                      element: <Organizations />,
                    },
                    {
                      path: "organization-charge-history",
                      element: <OrganizationChargeHistory />,
                    },
                    {
                      path: "access-codes",
                      element: <VoucherCodes />,
                    },
                    {
                      path: "tags",
                      element: <Tags />,
                    },
                    {
                      path: "trusted-repositories",
                      element: <TrustedRepositories />,
                    },
                    {
                      path: "import-logins",
                      element: <ImportLogins />,
                    },
                    {
                      path: "health-check",
                      element: <HealthCheck />,
                    },
                  ],
                },
              ],
            },
            {
              path: "/profile",
              element: <Profile />,
              children: [
                {
                  index: true,
                  element: <Navigate replace to="run-environments" />,
                },
                {
                  path: "run-environments",
                  element: <RunEnvironments />,
                },
                {
                  path: "credentials",
                  element: <Credentials />,
                },
                {
                  path: "user-groups",
                  element: <UserGroups />,
                },
                {
                  path: "integration-tokens",
                  element: <IntegrationTokens />,
                },
                {
                  element: <FeatureFlagProtectedRoutes featureFlag="FF_PAT" />,
                  children: [
                    {
                      path: "personal-access-tokens",
                      element: <PersonalAccessTokens />,
                    },
                    {
                      path: "connect-claude",
                      element: <ConnectAssistants />,
                    },
                  ],
                },
                {
                  path: "preferences",
                  element: <Preferences />,
                },
                {
                  path: "change-password",
                  element: <ChangePassword />,
                },
                {
                  path: "security",
                  element: <Security />,
                },
                {
                  path: "deactivate-account",
                  element: <DeactivateAccount />,
                },
              ],
            },
            {
              path: "/activity",
              element: <ActivityWrapper />,
            },
            {
              path: "/data",
              element: <Data />,
              children: [
                {
                  index: true,
                  element: <Navigate replace to="collections" />,
                },
                {
                  path: "collections",
                  element: <PremiumDatasetList />,
                },
                {
                  path: "/data/collections/:datasetId",
                  element: <PremiumDatasetFile />,
                },
                {
                  path: "/data/create-data-source",
                  element: <CreateDataSource />,
                },
                {
                  path: "/data/cloud-data-sources",
                  children: [
                    {
                      path: "/data/cloud-data-sources/amazon-s3",
                      element: <AmazonS3Wrapper />,
                    },
                    {
                      path: "/data/cloud-data-sources/google-cloud-storage",
                      element: <GoogleCloudStorageWrapper />,
                    },
                  ],
                },
                {
                  path: "/data/ssh/ssh-data",
                  element: <SshDataWrapper />,
                },
                {
                  path: "/data/remote-storage",
                  element: <RemoteStorageData />,
                },
                {
                  path: "/data/remote-storage/oauth/callback",
                  element: <OAuthCallback />,
                },
                {
                  path: "/data/token-based-data-sources",
                  children: [
                    {
                      path: "/data/token-based-data-sources/basespace",
                      element: <BaseSpace />,
                    },
                  ],
                },
                {
                  path: "/data/public-data-sources",
                  children: [
                    {
                      path: "/data/public-data-sources/geo/:geoId",
                      element: <GeoSource />,
                    },
                    {
                      path: "/data/public-data-sources/url",
                      element: <UrlData />,
                    },
                  ],
                },
              ],
            },
            ...pipelinesRoutes,
            {
              path: "/runs",
              element: <RunsHome />,
            },
            {
              path: "/runs/analytics",
              element: <RunsHome />,
            },
            {
              path: "/runs/schedulers",
              element: <RunsHome />,
            },
            {
              path: "/projects",
              element: <Projects />,
            },
            {
              path: "/projects/:projectId",
              element: <Project />,
              children: [
                {
                  index: true,
                  element: <Navigate replace to="dashboard" />,
                },
                {
                  path: "dashboard",
                  element: <Dashboard />,
                },
                {
                  path: "portal",
                  element: <Portal />,
                },
                {
                  path: "metadata",
                  element: <Metadata />,
                },
                {
                  path: "runs",
                  element: <Runs />,
                },
                {
                  path: "pipelines",
                  element: <Pipelines />,
                },
                {
                  path: "settings",
                  element: <ProjectSettings />,
                },
              ],
            },
            {
              path: "/runs/:runId",
              element: <RunWrapper />,
            },
            {
              // Legacy path: old run links used `/run/:runId` (singular).
              path: "/run/:runId",
              element: <RunRedirect />,
            },
            {
              path: "/pipelines/:pipelineId",
              element: (
                <ChangesDetectProvider>
                  <Pipeline />
                </ChangesDetectProvider>
              ),
              children: [
                {
                  index: true,
                  element: <Navigate replace to="description" />,
                },
                {
                  path: "description",
                  element: <PipelineDescription />,
                },
                {
                  path: "workflow",
                  element: <Workflow />,
                },
                {
                  path: "advanced",
                  element: <PipelineAdvanced />,
                },
                {
                  path: "settings",
                  element: <PipelineSettings />,
                },
                {
                  path: "git",
                  element: <PipelineGit />,
                },
                {
                  path: "provenance",
                  element: <PipelineProvenance />,
                },
              ],
            },
            {
              path: "/apps",
              element: <Apps />,
              children: [
                {
                  index: true,
                  element: <Navigate replace to="available-apps" />,
                },
                {
                  path: "available-apps",
                  element: <MyApps />,
                },
                {
                  path: "manage-status",
                  element: <ManageAppStatus />,
                },
                {
                  path: "cluster-launches",
                  element: <ClusterLaunches />,
                },
              ],
            },
            // Agent workspaces (agent-7-4, agent-d73). Each page renders nothing while the
            // installation keeps AGENT_WORKSPACE_JOBS_ENABLED off. The request route is the link an
            // AI client hands a person to approve a job.
            {
              path: "/workspaces",
              element: <AgentWorkspaces />,
            },
            {
              path: "/workspaces/:workspaceId",
              element: <AgentWorkspaceDetail />,
            },
            {
              path: "/workspaces/:workspaceId/requests/:requestId",
              element: <JobRequestReview />,
            },
            {
              path: "/workbench",
              element: <Workbenchs />,
              children: [
                {
                  index: true,
                  element: <Navigate replace to="available-workbench" />,
                },
                {
                  path: "available-workbench",
                  element: <MyWorkbenchs />,
                },
                {
                  path: "manage-status",
                  element: <ManageWorkbenchStatus />,
                },
              ],
            },
            {
              element: (
                <FeatureFlagProtectedRoutes featureFlag="FF_MODEL_SMITH" />
              ),
              children: [
                {
                  path: "/cortex",
                  element: <Cortex />,
                  children: [
                    {
                      index: true,
                      element: <Navigate replace to="model-garden" />,
                    },
                    {
                      path: "model-garden",
                      element: <ModelGarden />,
                    },
                    {
                      path: "model-skills",
                      element: <ModelSkill />,
                    },
                    {
                      path: "foundry-models",
                      element: <FoundryModels />,
                    },
                    {
                      path: "mcp-servers",
                      element: <McpServer />,
                    },
                    {
                      path: "knowledge-builder",
                      element: <KnowledgeBuilder />,
                    },
                  ],
                },
              ],
            },
            {
              path: "/organization",
              element: <OrganizationWrapper />,
            },
            {
              path: "/organization/:organizationId",
              element: <Organization />,
              children: [
                {
                  index: true,
                  element: <Navigate replace to="activity" />,
                },
                {
                  path: "activity",
                  element: <OrganizationActivity />,
                },
                {
                  path: "usage",
                  element: <OrganizationUsage />,
                },
                {
                  path: "invoices",
                  element: <InvoicesRoute />,
                },
                {
                  path: "run-environments",
                  element: <OrganizationRunEnvironments />,
                },
                {
                  path: "users",
                  element: <OrganizationUsers />,
                },
                {
                  path: "settings",
                  element: <OrganizationSettings />,
                },
              ],
            },
            {
              path: "/sample-trackers",
              element: <SampleTrackers />,
            },
            {
              path: "/sample-trackers/:projectId",
              element: <Metadata showPageHeading={true} />,
            },
            {
              path: "/sample-trackers/:projectId/sample-explorer",
              element: <Metadata showPageHeading={true} />,
            },
            {
              path: "/sample-trackers/:projectId/events",
              element: <Metadata showPageHeading={true} />,
            },
            {
              path: "/sample-trackers/:projectId/events/:eventId/*",
              element: <Metadata showPageHeading={true} />,
            },
            {
              path: ROUTES.SCAN,
              element: <ScanPage />,
            },
            {
              // A splat, so a code holding an encoded slash still lands here; the page reads
              // the raw path itself (see parseAliasPath).
              path: ROUTES.ALIAS,
              element: <AliasRedirect />,
            },
          ],
        },
      ],
    },
  ],
  {
    basename: Config.ROUTER_BASENAME,
  }
);

const AppRoutes = () => {
  // Comment out for now until we fix status codes being returned across the app
  /* const navigate = useNavigate();
  useEffect(() => {
    setupInterceptors(navigate);
  }, [navigate]); */

  return <RouterProvider router={router} />;
};
export default AppRoutes;
